import { DefinedInstallationType, INSTALLATION_TYPE } from 'app/shared/models/location-details';
import { LocationStatus } from 'app/shared/models/locations';
import { LocationCommunicationType, MonitorSeriesNames } from '../../../location-card.constants';
import { NormalizedFormState } from '../../config.models';
import { FieldType } from '../../models/field-models';
import { findFieldById, findSectionById } from '../base-utils';
import {
    BasicInfoCommonFieldIds,
    ChannelInstallationFieldIds,
    FlumeInstallationFieldIds,
    PipeInstallationFieldIds,
    WeirInstallationFieldIds,
} from '../common.models';
import { EdgeDxInstallationPayload, EdgeDxLocationBaseInfo, EdgeDxLocationPayload } from './base-api-models';
import { ElevationInstallationFieldIds, INSTALLATION_DEFAULT_CONSTANTS } from './installation-types/config-models';
import { EdgeDxLocationCardState } from './models';

export function gatherEdgeDxBasicInfoPayload(state: EdgeDxLocationCardState): EdgeDxLocationPayload[] {
    const form = state.formState;
    const rainGaugeName = getStringValue(form, BasicInfoCommonFieldIds.AssignedRainGauge, FieldType.Autocomplete);
    const rainGauge = state.options.rainGauges?.data.find((location) => location.locationName === rainGaugeName);
    const details = state.locationDetails[0];
    const status = getRequiredNumberValue(form, BasicInfoCommonFieldIds.Status, FieldType.Select);
    if (
        status !== LocationStatus.Active &&
        status !== LocationStatus.Inactive &&
        status !== LocationStatus.Maintenance
    ) {
        throw new Error('Invalid location status.');
    }

    const basicInfo: EdgeDxLocationBaseInfo = {
        locationName: getRequiredStringValue(form, BasicInfoCommonFieldIds.LocationName),
        assignedRainGaugeId:
            rainGauge?.locationId ??
            (rainGaugeName && rainGaugeName === details?.assignedRainGaugeLocationName
                ? details.assignedRainGaugeLocationId ?? null
                : null),
        description: getStringValue(form, BasicInfoCommonFieldIds.Description),
        latitude: getNumberValue(form, BasicInfoCommonFieldIds.Latitude),
        longitude: getNumberValue(form, BasicInfoCommonFieldIds.Longitude),
        communicationType: LocationCommunicationType.External,
        ipaddress: null,
        manholeaddress: getStringValue(form, BasicInfoCommonFieldIds.ManholeAddress),
        manholedepth: getRequiredNumberValue(form, BasicInfoCommonFieldIds.ManholeDepth),
        installationDate: findFieldById(form, BasicInfoCommonFieldIds.InstallationDate, FieldType.Date)?.value ?? null,
        qFinalEntityID: getNumberValue(form, BasicInfoCommonFieldIds.QFinalEntityID, FieldType.Select),
        serialnumber: getRequiredStringValue(form, BasicInfoCommonFieldIds.SerialNumber),
        series: MonitorSeriesNames.edgeDx,
        status,
        customProperties: getCustomProperties(form),
    };

    return [{ ...basicInfo, ...getInstallationPayload(form) }];
}

function getCustomProperties(form: NormalizedFormState): { key: string; value: string }[] {
    const section = findSectionById(form, BasicInfoCommonFieldIds.CustomPropertiesSection);
    if (!section) {
        return [];
    }

    return section.rows.map((row) => {
        const keyId = row.fields.find((id) => section.fields[id]?.name === 'key');
        const valueId = row.fields.find((id) => section.fields[id]?.name === 'value');
        if (!keyId || !valueId) {
            throw new Error('Tag fields are missing.');
        }
        return {
            key: getRequiredStringValue(form, keyId, FieldType.Autocomplete),
            value: getRequiredStringValue(form, valueId),
        };
    });
}

function getInstallationPayload(form: NormalizedFormState): EdgeDxInstallationPayload {
    const selected = getRequiredStringValue(form, BasicInfoCommonFieldIds.InstallationType, FieldType.Select);
    const [type, shape] = selected.split(':').map((value) => value.trim());
    const installationID = getInstallationId(type);
    if (!shape || !Number.isInteger(Number(shape))) {
        throw new Error('Invalid installation shape.');
    }

    const installation = {
        installationID,
        installationtypeid: installationID,
        installationShapeType: shape,
    };
    const metadata = {
        ...INSTALLATION_DEFAULT_CONSTANTS,
        lookupDesc: type,
        installationType: type,
        MonitoringPoint: 1,
        installationID,
        shapeType: shape,
        installationShapeType: shape,
        depthSensor: 100,
        velocitySensor: 100,
    };

    switch (installationID) {
        case DefinedInstallationType.Pipe:
            return {
                ...installation,
                pipe: {
                    ...metadata,
                    height: getRequiredNumberValue(form, PipeInstallationFieldIds.Height),
                    width: getRequiredNumberValue(form, PipeInstallationFieldIds.Width),
                    hc: getRequiredNumberValue(form, PipeInstallationFieldIds.Hc),
                    roughness: getRequiredNumberValue(form, PipeInstallationFieldIds.Roughness),
                    effectiveRoughness: getRequiredNumberValue(form, PipeInstallationFieldIds.EffectiveRoughness),
                    slope: getRequiredNumberValue(form, PipeInstallationFieldIds.Slope),
                    frictionFactor: getRequiredNumberValue(form, PipeInstallationFieldIds.FrictionFactor),
                    nominalDiameter: getRequiredNumberValue(form, PipeInstallationFieldIds.NominalDiameter),
                    startDate: findFieldById(form, PipeInstallationFieldIds.StartDate, FieldType.Date)?.value ?? null,
                },
            };
        case DefinedInstallationType.Weir:
            return {
                ...installation,
                weir: {
                    ...metadata,
                    range: getRequiredNumberValue(form, WeirInstallationFieldIds.Range),
                    capacity: getRequiredNumberValue(form, WeirInstallationFieldIds.Capacity),
                    height: getRequiredNumberValue(form, WeirInstallationFieldIds.Height),
                    coefficient: getRequiredNumberValue(form, WeirInstallationFieldIds.Coefficient),
                    length: getRequiredNumberValue(form, WeirInstallationFieldIds.Length),
                    breadth: getRequiredNumberValue(form, WeirInstallationFieldIds.Breadth),
                },
            };
        case DefinedInstallationType.Flume:
            return {
                ...installation,
                flume: {
                    ...metadata,
                    range: getRequiredNumberValue(form, FlumeInstallationFieldIds.Range),
                    capacity: getRequiredNumberValue(form, FlumeInstallationFieldIds.Capacity),
                    width: getRequiredNumberValue(form, FlumeInstallationFieldIds.Width),
                },
            };
        case DefinedInstallationType.Channel: {
            const numberOfEntries = getRequiredNumberValue(form, ChannelInstallationFieldIds.NumberOfEntries);
            const entries = Array.from({ length: numberOfEntries }, (_, index) => ({
                depth: getRequiredNumberValue(form, `${ChannelInstallationFieldIds.Depth}-${index}`),
                quantity: getRequiredNumberValue(form, `${ChannelInstallationFieldIds.Quantity}-${index}`),
            }));
            return {
                ...installation,
                channel: {
                    ...metadata,
                    range: getRequiredNumberValue(form, ChannelInstallationFieldIds.Range),
                    numberOfEntries,
                    entries,
                },
            };
        }
        case DefinedInstallationType.Elevation:
            return {
                ...installation,
                elevationInstallation: {
                    ...metadata,
                    datum: getRequiredStringValue(form, ElevationInstallationFieldIds.Datum),
                    physicalOffset: getRequiredNumberValue(form, ElevationInstallationFieldIds.PhysicalOffset),
                    referencePoint: getRequiredNumberValue(form, ElevationInstallationFieldIds.ReferencePoint),
                },
            };
        default:
            throw new Error('Unsupported EDGE-DX installation type.');
    }
}

function getInstallationId(type: string): DefinedInstallationType {
    switch (type) {
        case INSTALLATION_TYPE.PIPE:
            return DefinedInstallationType.Pipe;
        case INSTALLATION_TYPE.WEIR:
            return DefinedInstallationType.Weir;
        case INSTALLATION_TYPE.FLUME:
            return DefinedInstallationType.Flume;
        case INSTALLATION_TYPE.CHANNEL:
            return DefinedInstallationType.Channel;
        case INSTALLATION_TYPE.ELEVATION:
            return DefinedInstallationType.Elevation;
        default:
            throw new Error('Unsupported EDGE-DX installation type.');
    }
}

function getStringValue(
    form: NormalizedFormState,
    fieldId: string,
    type: FieldType.Text | FieldType.Autocomplete | FieldType.Select = FieldType.Text,
): string | null {
    const value = findFieldById(form, fieldId, type)?.value;
    if (value === null || value === undefined) {
        return null;
    }
    if (typeof value !== 'string') {
        throw new Error(`Invalid text value for ${fieldId}.`);
    }
    return value;
}

function getRequiredStringValue(
    form: NormalizedFormState,
    fieldId: string,
    type: FieldType.Text | FieldType.Autocomplete | FieldType.Select = FieldType.Text,
): string {
    const value = getStringValue(form, fieldId, type);
    if (value === null || value.trim() === '') {
        throw new Error(`Missing value for ${fieldId}.`);
    }
    return value;
}

function getNumberValue(
    form: NormalizedFormState,
    fieldId: string,
    type: FieldType.Number | FieldType.Select = FieldType.Number,
): number | null {
    const value = findFieldById(form, fieldId, type)?.value;
    if (value === null || value === undefined || value === '') {
        return null;
    }
    if ((typeof value !== 'number' && typeof value !== 'string') || !Number.isFinite(Number(value))) {
        throw new Error(`Invalid numeric value for ${fieldId}.`);
    }
    return Number(value);
}

function getRequiredNumberValue(
    form: NormalizedFormState,
    fieldId: string,
    type: FieldType.Number | FieldType.Select = FieldType.Number,
): number {
    const value = getNumberValue(form, fieldId, type);
    if (value === null) {
        throw new Error(`Missing value for ${fieldId}.`);
    }
    return value;
}
